import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CalendarCheck, Check, FolderOpen, HelpCircle, ShieldCheck, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const monthOptions = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
];

export default function PasspFattureVsPrimaNota() {
  const now = new Date();
  const [azienda, setAzienda] = useState('all');
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [anniOptions, setAnniOptions] = useState([]);
  const [rows, setRows] = useState([]);

  const fetchOptions = async () => {
    setOptionsLoading(true);
    try {
      const res = await api.get('/passp-fatture/controlli/options');
      const data = res.data || {};
      const aziende = Array.isArray(data.aziende) ? data.aziende : [];
      const anni = Array.isArray(data.anni) ? data.anni : [];
      setAziendeOptions(aziende);
      setAnniOptions(anni);
      if (!anni.includes(anno) && anni.length > 0) {
        setAnno(Number(anni[0]));
      }
    } catch (error) {
      console.error('Error fetching Passpartout checks options:', error);
      toast.error('Errore nel caricamento opzioni Controlli Passpartout');
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        anno,
        mese,
      };
      if (azienda !== 'all') params.azienda = azienda;
      const res = await api.get('/passp-fatture/controlli/fatture-vs-prima-nota', { params });
      const data = res.data || {};
      setRows(Array.isArray(data.days) ? data.days : []);
    } catch (error) {
      console.error('Error fetching Passpartout daily checks:', error);
      toast.error('Errore nel caricamento dettaglio giornaliero Controlli');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [azienda, anno, mese]);

  const selectedAziendaLabel = useMemo(() => {
    if (azienda === 'all') return '—';
    const found = aziendeOptions.find((a) => a.code === azienda);
    return found?.name || azienda;
  }, [azienda, aziendeOptions]);

  const selectedMeseLabel = useMemo(
    () => monthOptions.find((m) => m.value === Number(mese))?.label || String(mese),
    [mese]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#eef2ff',
            color: '#4f46e5',
          }}
        >
          <ShieldCheck size={22} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Controlli • Fatture Vs Prima Nota
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Riepilogo giornaliero comparativo tra movimenti Fatture e Prima Nota.
          </Typography>
        </Box>
      </Box>

      <StyledCard sx={{ mb: 2 }}>
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Azienda</InputLabel>
            <Select value={azienda} label="Azienda" onChange={(e) => setAzienda(e.target.value)}>
              <MenuItem value="all">Seleziona un&apos;opzione</MenuItem>
              {aziendeOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Anno</InputLabel>
            <Select value={anno} label="Anno" onChange={(e) => setAnno(Number(e.target.value))}>
              {anniOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>

        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, gap: 2 }}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.2,
                height: 'fit-content',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.8 }}>
                <FolderOpen size={16} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {anno}
                </Typography>
              </Box>
              <Box sx={{ pl: 1.5, mt: 0.4 }}>
                {monthOptions.map((item) => {
                  const selected = Number(mese) === item.value;
                  return (
                    <Box
                      key={item.value}
                      component="button"
                      type="button"
                      onClick={() => setMese(item.value)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        border: 0,
                        textAlign: 'left',
                        px: 1.2,
                        py: 0.8,
                        borderRadius: 1,
                        backgroundColor: selected ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        fontSize: 16,
                        color: selected ? 'text.primary' : 'text.secondary',
                        fontWeight: selected ? 700 : 500,
                        '&:hover': {
                          backgroundColor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CalendarCheck size={18} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Dettaglio giornaliero
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Filtri: Azienda <strong>{selectedAziendaLabel}</strong>, Anno <strong>{anno}</strong>, Mese{' '}
                <strong>{selectedMeseLabel}</strong>
              </Typography>

              {optionsLoading || loading ? <LoaderProgress /> : null}

              {!loading && !optionsLoading ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Giorno</TableCell>
                        <TableCell align="right">Fatture</TableCell>
                        <TableCell align="right">Note Credito</TableCell>
                        <TableCell align="right">PN Dare</TableCell>
                        <TableCell align="right">PN Avere</TableCell>
                        <TableCell align="right">PN Saldo</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            Controllo 1
                            <Tooltip title="Controllo 1: |Fatture| + |Note Credito| deve essere uguale a PN Dare">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>
                                <HelpCircle size={14} />
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            Controllo 2
                            <Tooltip title="Controllo 2: PN Saldo (PN Dare - PN Avere) deve essere uguale a 0">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}>
                                <HelpCircle size={14} />
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.giorno}>
                          <TableCell>{row.giorno}</TableCell>
                          <TableCell align="right">{formatCurrency(row.fatture)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.noteCredito)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.pnDare)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.pnAvere)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.pnSaldo)}</TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: row.controllo1 === 'OK' ? 'success.main' : 'error.main',
                              }}
                            >
                              {row.controllo1 === 'OK' ? <Check size={18} /> : <X size={18} />}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: row.controllo2 === 'OK' ? 'success.main' : 'error.main',
                              }}
                            >
                              {row.controllo2 === 'OK' ? <Check size={18} /> : <X size={18} />}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Alert severity="info">Nessun dato disponibile per i filtri selezionati.</Alert>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Box>
          </Box>
        </Box>
      </StyledCard>
    </Box>
  );
}
